import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { format as formatDate } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { getStatusColor } from '../utils/statusColors';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

function AdminCalendar() {
    const [bookings, setBookings] = useState([]);
    const navigate = useNavigate();

    const [view, setView] = useState(window.innerWidth < 768 ? 'day' : 'month');

    useEffect(() => {
        const handleResize = () => {
            setView(window.innerWidth < 768 ? 'day' : 'month');
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchBookings = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            const response = await axios.get(`${API_URL}/api/bookings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBookings(response.data);
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('token');
                navigate('/login');
            }
        }
    };

    useEffect(() => {
        fetchBookings();
        const interval = setInterval(fetchBookings, 20000);
        return () => clearInterval(interval);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const events = bookings.map((b) => {
        const start = new Date(b.pickup_datetime);
        const end = new Date(start.getTime() + 30 * 60000); // 30-minute block
        const driver = b.driver_name ? b.driver_name : 'Unassigned';
        return {
            id: b.id,
            title: `#${b.id} [${driver}] ${b.customer_name} — ${b.pickup_location} → ${b.destination}`,
            start,
            end,
            status: b.status,
        };
    });

    const eventStyleGetter = (event) => ({
        style: {
            backgroundColor: getStatusColor(event.status),
            borderRadius: '6px',
            color: '#fff',
            border: 'none',
            fontSize: '0.8125rem',
        },
    });

    const calendarFormats = {
        dateFormat: 'd',
        dayFormat: 'EEE d',
        monthHeaderFormat: (date) => formatDate(date, 'MMMM yyyy'),
        dayHeaderFormat: (date) => formatDate(date, 'EEEE, MMMM d'),
        dayRangeHeaderFormat: ({ start, end }) =>
            `${formatDate(start, 'MMMM d')} – ${formatDate(end, 'MMMM d, yyyy')}`,
        agendaDateFormat: (date) => formatDate(date, 'EEE, MMM d'),
        agendaHeaderFormat: ({ start, end }) =>
            `${formatDate(start, 'MMMM d')} – ${formatDate(end, 'MMMM d, yyyy')}`,
    };
    return (
        <div className="dashboard-page admin-compact">
            <div className="dashboard-container" style={{ maxWidth: '1100px' }}>
                <div className="dashboard-header">
                    <div>
                        <h1><CalendarDays size={26} className="page-icon" /> Calendar</h1>
                        <p>All bookings by day</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={handleLogout} className="logout-btn">Logout</button>
                    </div>
                </div>

                <div className="filters" style={{ marginBottom: '24px' }}>
                    <Link to="/admin/dashboard" style={{ textDecoration: 'none' }}>Bookings</Link>
                    <Link to="/admin/drivers" style={{ textDecoration: 'none' }}>Drivers</Link>
                    <Link to="/admin/calendar" className="active" style={{ textDecoration: 'none' }}>Calendar</Link>
                </div>

                <div className="calendar-panel">
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: 480 }}
                        eventPropGetter={eventStyleGetter}
                        view={view}
                        onView={setView}
                        views={[Views.MONTH, Views.DAY, Views.AGENDA]}
                        formats={calendarFormats}
                    />
                </div>
            </div>
        </div>
    );
}

export default AdminCalendar;